import React, { useCallback, useEffect, useMemo, useState } from 'react'
import _ from 'lodash'
import {
  Box,
  VStack,
  FormControl as ChakraFormControl,
  FormErrorMessage as ChakraFormErrorMessage,
} from '@chakra-ui/react'
import type { OnChangeValue, SingleValue } from 'react-select'
import { IKReactSelect } from '../../../../ui-kit'

import type { InputFormProps as Props } from '../../../interfaces'
import { getFieldsMapping } from '../../../../utils/mediaLibrary'
import OperatorMenu from '../OperatorMenu'
import { useDebounce } from '../../../hooks/useDebounce'

interface FolderOption {
  label: string
  value: string
  fullPath: string
}

// Helper function to format path for display
// - Shows full path if it has ≤ 2 folders OR if it's short enough to fit
// - For exactly 3 folders: shows first two + "..."
// - For > 3 folders: shows first + "..." + last
const formatPathForDisplay = (fullPath: string): string => {
  const pathParts = fullPath.split('/').filter(Boolean)
  
  if (pathParts.length === 0) {
    return '/'
  }
  
  const MAX_TOTAL_PATH_LENGTH = 40 // If full path fits within this, show it fully
  const fullPathString = `/${pathParts.join('/')}`
  
  // Show full path if it has ≤ 2 folders OR if it's short enough to fit
  if (pathParts.length <= 2 || fullPathString.length <= MAX_TOTAL_PATH_LENGTH) {
    return fullPathString
  }
  
  const MAX_FOLDER_NAME_LENGTH = 20 // Max characters per folder name before ellipsizing
  const ELLIPSIS = '...'
  
  const ellipsizeFolderName = (name: string, maxLength: number): string => {
    if (name.length <= maxLength) {
      return name
    }
    return `${name.substring(0, maxLength - ELLIPSIS.length)}${ELLIPSIS}`
  }
  
  // For exactly 3 folders: show first two + "..."
  if (pathParts.length === 3) {
    const firstFolder = ellipsizeFolderName(pathParts[0], MAX_FOLDER_NAME_LENGTH)
    const secondFolder = ellipsizeFolderName(pathParts[1], MAX_FOLDER_NAME_LENGTH)
    return `/${firstFolder}/${secondFolder}/...`
  }
  
  // For > 3 folders: show first + "..." + second last
  const firstFolder = ellipsizeFolderName(pathParts[0], MAX_FOLDER_NAME_LENGTH)
  const secondLastFolder = ellipsizeFolderName(pathParts[pathParts.length - 2], MAX_FOLDER_NAME_LENGTH)
  
  return `/${firstFolder}/.../${secondLastFolder}/...`
}

const PathInputForm = (props: Props): React.ReactElement => {
  const {
    searchQueryJson,
    validationObject,
    groupId,
    filterId,
    initialFocusRef,
    setSearchQueryJson,
  } = props

  const [search, setSearch] = useState<string>(() =>
    typeof searchQueryJson.filterGroups[groupId]?.expressions[filterId]
      ?.value === 'string'
      ? searchQueryJson.filterGroups[groupId]?.expressions[filterId]?.value
          .split('/')
          .filter(Boolean)
          .pop() ??
        searchQueryJson.filterGroups[groupId]?.expressions[filterId]?.value
      : '',
  )
  const [folderOptions, setFolderOptions] = useState<FolderOption[]>([])
  const [dirty, setDirty] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const debouncedSearch = useDebounce(search, 500)

  const expression = useMemo(
    () => searchQueryJson.filterGroups[groupId]?.expressions[filterId],
    [searchQueryJson, groupId, filterId],
  )

  const error: string | null = useMemo(
    () => validationObject.errors[groupId][filterId],
    [validationObject, groupId, filterId],
  )

  const showError = useMemo(() => dirty && Boolean(error), [dirty, error])

  // Fetch folder options for auto-complete
  useEffect(() => {
    if (!debouncedSearch) {
      setFolderOptions([])
      setIsLoading(false)
      return
    }

    const params = new URLSearchParams({
      skip: '0',
      limit: '20',
      sort: 'DESC_RELEVANCE',
      searchQuery: `(type = folder AND (name HAS "${debouncedSearch}"))`,
    }).toString()

    let cancelled = false
    setIsLoading(true)
    fetch(`/api/v2/files?${params}`)
      .then(res => (res.ok ? res.json() : []))
      .then((data: any[]) => {
        if (!cancelled) {
          setFolderOptions(
            data.map(f => ({
              label: f.name,
              value: f.folderPath,
              fullPath: f.folderPath,
            })),
          )
          setIsLoading(false)
        }
      })
      .catch(() => {
        if (!cancelled) {
          setFolderOptions([])
          setIsLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [debouncedSearch])

  const handleOperatorChange = useCallback(
    (newOperator: string) => {
      const newSearchQueryJson = _.cloneDeep(searchQueryJson)
      newSearchQueryJson.filterGroups[groupId].expressions[filterId].operator =
        newOperator
      newSearchQueryJson.filterGroups[groupId].expressions[filterId].value =
        getFieldsMapping({ fieldName: expression.fieldName }).getInitialValue(
          newOperator,
        )
      setSearchQueryJson(newSearchQueryJson)
    },
    [searchQueryJson, groupId, filterId],
  )

  const handleValueChange = useCallback(
    (newValue: string | string[]) => {
      const newSearchQueryJson = _.cloneDeep(searchQueryJson)
      newSearchQueryJson.filterGroups[groupId].expressions[filterId].value =
        newValue
      setSearchQueryJson(newSearchQueryJson)
    },
    [searchQueryJson, groupId, filterId],
  )
  const handleSelectChange = useCallback(
    (option: SingleValue<FolderOption> | null) => {
      if (option && option.value) {
        handleValueChange(option.value)
        setSearch(option.label)
      } else {
        handleValueChange('')
        setSearch('')
      }
      setDirty(true)
    },
    [handleValueChange],
  )

  const handleMultiSelectChange = useCallback(
    (options: OnChangeValue<FolderOption, true>) => {
      const values = options ? options.map(opt => opt.value) : []
      handleValueChange(values)
      setDirty(true)
    },
    [handleValueChange],
  )

  useEffect(() => {
    initialFocusRef.current?.focus()
  }, [initialFocusRef, expression.operator])

  const isSingleOperator = useMemo(
    () => ['IS', 'IS_NOT', 'STARTS_WITH'].includes(expression.operator),
    [expression.operator],
  )

  const isMultiOperator = useMemo(
    () =>
      ['IS_ONE_OF', 'IS_NONE_OF'].includes(expression.operator) &&
      Array.isArray(expression.value),
    [expression.operator, expression.value],
  )

  const singleValueOption: FolderOption | null = useMemo(() => {
    if (!isSingleOperator) {
      return null
    }
    if (typeof expression.value !== 'string' || !expression.value) {
      return null
    }
    const full = expression.value
    const label = full.split('/').filter(Boolean).pop() ?? full
    return { label, value: full, fullPath: full }
  }, [isSingleOperator, expression.value])

  // Sync search input when singleValueOption changes (e.g., when form opens)
  useEffect(() => {
    if (isSingleOperator) {
      if (singleValueOption) {
        setSearch(singleValueOption.label)
      } else {
        setSearch('')
      }
    }
  }, [isSingleOperator, singleValueOption])

  const multiValueOptions: FolderOption[] = useMemo(() => {
    if (!isMultiOperator) {
      return []
    }
    const arr = expression.value as string[]
    return arr.map(val => ({
      value: val,
      label: val.split('/').filter(Boolean).pop() ?? val,
      fullPath: val,
    }))
  }, [isMultiOperator, expression.value])

  const formatOptionLabel = useCallback(
    (data: unknown, _meta?: { context: string }) => {
      const option = data as FolderOption
      const isSelected =
        (isSingleOperator && singleValueOption?.value === option.value) ||
        (isMultiOperator && multiValueOptions.some(selected => selected.value === option.value))
      
      // Format the full path for display (first two folders + "..." if > 2 folders)
      const formattedPath = formatPathForDisplay(option.fullPath)
      
      return (
        <Box width="full" overflow="hidden">
          <Box
            fontWeight="normal"
            whiteSpace="nowrap"
            overflow="hidden"
            textOverflow="ellipsis"
            maxWidth="100%"
          >
            {option.label}
          </Box>
          <Box
            fontSize="sm"
            color={isSelected ? 'gray.700' : 'gray.500'}
            whiteSpace="nowrap"
            overflow="hidden"
            textOverflow="ellipsis"
            maxWidth="100%"
          >
            {formattedPath}
          </Box>
        </Box>
      )
    },
    [isSingleOperator, isMultiOperator, singleValueOption, multiValueOptions],
  )

  const singleSelectNode = useMemo(() => {
    if (!isSingleOperator) {
      return null
    }
    return (
      <Box width="full">
        <IKReactSelect
          ref={initialFocusRef}
          variant="compact"
          placeholder="Type to search folders..."
          valueLabelOnly
          value={singleValueOption}
          options={folderOptions}
          onInputChange={(val: string) => {
            setDirty(true)
            setSearch(val)
          }}
          onFocus={() => {
            if (singleValueOption) {
              setSearch(singleValueOption.label)
            }
          }}
          isLoading={isLoading}
          onChange={handleSelectChange}
          formatOptionLabel={formatOptionLabel}
          isClearable
          isSearchable
          noOptionsMessage={() => 'No folders found'}
          loadingMessage={() => 'Searching folders...'}
        />
      </Box>
    )
  }, [
    isSingleOperator,
    initialFocusRef,
    singleValueOption,
    folderOptions,
    search,
    handleSelectChange,
    formatOptionLabel,
    isLoading,
  ])

  const multiSelectNode = useMemo(() => {
    if (!isMultiOperator) {
      return null
    }
    return (
      <Box width="full">
        <IKReactSelect
          ref={initialFocusRef}
          variant="compact"
          placeholder="Type to search folders..."
          isMulti
          value={multiValueOptions}
          options={folderOptions}
          onInputChange={(val: string) => {
            setDirty(true)
            setSearch(val)
          }}
          isLoading={isLoading}
          onChange={handleMultiSelectChange}
          formatOptionLabel={formatOptionLabel}
          isClearable
          isSearchable
          noOptionsMessage={() => 'No folders found'}
          loadingMessage={() => 'Searching folders...'}
        />
      </Box>
    )
  }, [
    isMultiOperator,
    initialFocusRef,
    multiValueOptions,
    folderOptions,
    handleMultiSelectChange,
    formatOptionLabel,
    isLoading,
  ])

  return (
    <ChakraFormControl width="full" isInvalid={showError}>
      <VStack alignItems="start" width="full" spacing="0.5em">
        <VStack alignItems="stretch" width="full" spacing="0.5em">
          <OperatorMenu
            width="full"
            expression={expression}
            handleOperatorChange={handleOperatorChange}
          />

          {singleSelectNode}

          {multiSelectNode}
        </VStack>

        <ChakraFormErrorMessage maxWidth="full">{error}</ChakraFormErrorMessage>
      </VStack>
    </ChakraFormControl>
  )
}

export default PathInputForm
